import React from 'react';
import { BaseEditor, BaseRange, Descendant, Text as SlateText } from 'slate';
import { ReactEditor } from 'slate-react';
import { EditableProps } from 'slate-react/dist/components/editable';

export type RichTextBoxToolbarButtonBaseProps = {
  icon?: JSX.Element | JSX.Element[] | string[] | string | null;
  label?: JSX.Element | string | null;
  attrs?: React.HTMLAttributes<HTMLButtonElement> & {
    disable?: boolean | undefined;
  };
};

export type SerializeRichTextValueToStringResult = {
  isPlainText: boolean;
  value: string | null;
};

/**
 * This is a bit field enum, each value is a separate bit
 */
export enum RichTextBoxFormatValue {
  none = 0,
  bold = 1,
  italic = 2,
  underline = 4,
  //todo add more bit flags for more formats
}

export type RichTextBoxElementListType = 'numbered-list' | 'bulleted-list';
export type RichTextBoxElementType =
  | 'paragraph'
  | 'list-item'
  | RichTextBoxElementListType;

export type RichTextBoxProps = {
  value?: string | undefined;
  disabled?: boolean;
  rows?: number;
  onChange?: ((value: string | undefined | null) => void) | undefined;
  onBlur?: React.FocusEventHandler<HTMLDivElement> | undefined;
  placeholder?: string | undefined;
  className?: string | undefined;
  toolbarProps?: React.HTMLAttributes<HTMLSpanElement>;
  editableProps?: EditableProps;
  /** This property accepts a callback that operates with an HTMLElement (like RHF RefCallback), or a generic React ref that can reference any type of element,
		or a specific React ref that references a div element.
	*/
  inputRef?:
    | ((el: HTMLElement) => void)
    | React.Ref<any>
    | React.RefObject<HTMLDivElement>;
  isSMSText?: boolean | undefined;
  isToolbarDisabled?: boolean | undefined;
  isTrasientKeywords?: boolean | undefined;
};

export type RichTextBoxEditorKeyDownEvent = (
  event: React.KeyboardEvent<Element>,
) => void;

export type FocusedTextNode = {
  textNode: SlateText;
  cursorOffset: number;
};

export interface RichTextBoxEditor extends BaseEditor, ReactEditor {
  uid: string;
  onSelect: () => void;
  isSelectionSingleCursor: (selection: BaseRange) => boolean;
  isSelectionAtEndOfTextNode: (selection: BaseRange) => boolean;
  closeMacros: (deleteSlash?: boolean) => void;
  getFocusedTextNodeAtCursor: () => FocusedTextNode | null;
  onKeyDown: RichTextBoxEditorKeyDownEvent;
  changeBlockIndent: (decrease?: boolean, indentationLevel?: number) => void;
  insertMacro: (html: string) => boolean;
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const RichTextBoxEditor = {
  ...ReactEditor,
};

export type RichTextBoxText = {
  isMacroSearch?: boolean;
  format?: RichTextBoxFormatValue | null;
  color?: string | undefined;
  text: string;
};

//these are interfaces because only interfaces can merge declarations for discriminating unions
//create a common base element where type can be any of the element types we support
export interface RichTextBoxBaseElement {
  type: RichTextBoxElementType;
  inline?: boolean;
  indentationLevel?: number;
  attrs?: Omit<React.HTMLAttributes<HTMLElement>, 'children'>;
  children?: Descendant[] | undefined;
}

export interface RichTextBoxParagraphElement extends RichTextBoxBaseElement {
  type: 'paragraph';
  attrs?: Omit<React.HTMLAttributes<HTMLParagraphElement>, 'children'>;
}

export interface RichTextBoxBulletedListElement extends RichTextBoxBaseElement {
  type: 'bulleted-list';
  attrs?: Omit<React.HTMLAttributes<HTMLUListElement>, 'children'>;
}

export interface RichTextBoxNumberedListElement extends RichTextBoxBaseElement {
  type: 'numbered-list';
  attrs?: Omit<React.HTMLAttributes<HTMLOListElement>, 'children'>;
}

export interface RichTextBoxListItemElement extends RichTextBoxBaseElement {
  type: 'list-item';
  attrs?: Omit<React.HTMLAttributes<HTMLLIElement>, 'children'>;
}

//combine all the RichTextEditorElements we support users creating in the rich text editor
export type RichTextBoxElement =
  | RichTextBoxParagraphElement
  | RichTextBoxBulletedListElement
  | RichTextBoxListItemElement
  | RichTextBoxNumberedListElement;
//declare all our custom types in a way that slate can see them (this is in the slate-react documentation)
declare module 'slate' {
  interface CustomTypes {
    Editor: RichTextBoxEditor;
    Element: RichTextBoxElement;
    Text: RichTextBoxText;
  }
}
